import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { userAuthHeader } from '../../_helpers';

/**
 * Deposit Account Stats Component for Dashboard
 * Self-contained: fetches its own data, has its own loading/error states
 * Does not affect the rest of the dashboard if it fails
 */

const STATUS_CONFIG = {
  draft: {
    label: 'Brouillons',
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    gradient: 'from-slate-500 to-slate-600',
    bgLight: 'bg-slate-50',
    borderColor: 'border-slate-200',
    textColor: 'text-slate-700',
    iconBg: 'bg-slate-100',
  },
  signed: {
    label: 'Signés',
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    gradient: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-700',
    iconBg: 'bg-emerald-100',
  },
  submitted: {
    label: 'Soumis à l\'INPI',
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    ),
    gradient: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    iconBg: 'bg-blue-100',
  },
  validated: {
    label: 'Validés',
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    gradient: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    iconBg: 'bg-green-100',
  },
  rejected: {
    label: 'Rejetés',
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    gradient: 'from-red-500 to-red-600',
    bgLight: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    iconBg: 'bg-red-100',
  },
};

function StatCard({ label, count, icon, gradient, bgLight, borderColor, textColor, iconBg }) {
  return (
    <div className={`relative overflow-hidden rounded-xl border ${borderColor} ${bgLight} p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group`}>
      {/* Gradient accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
      
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] sm:text-xs font-medium text-gray-500  tracking-wider mb-1">{label}</p>
          <p className={`text-2xl sm:text-3xl font-bold ${textColor} tabular-nums`}>{count}</p>
        </div>
        <div className={`${iconBg} rounded-xl p-2 sm:p-2.5 ${textColor} opacity-70 group-hover:opacity-100 transition-opacity`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function RecentFilingRow({ filing }) {
  const statusColors = {
    draft: 'bg-slate-100 text-slate-700',
    signed: 'bg-emerald-100 text-emerald-700',
    submitted: 'bg-blue-100 text-blue-700',
    validated: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };

  const statusLabels = {
    draft: 'Brouillon',
    signed: 'Signé',
    submitted: 'Soumis',
    validated: 'Validé',
    rejected: 'Rejeté',
  };

  const statusKey = filing.status && filing.status.trim() !== '' ? filing.status : 'draft';

  return (
    <Link
      to={`/prouser/depot-comptes/${filing.id}`}
      className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer"
    >
      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-[#1cbe93] to-[#17a37e] flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xs sm:text-sm font-bold">
            {(filing.companyName || '?').charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{filing.companyName || 'N/A'}</p>
          <p className="text-[10px] sm:text-xs text-gray-500">
            {filing.siren || 'N/A'} · {filing.closingDate || 'N/A'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full ${statusColors[statusKey] || 'bg-gray-100 text-gray-700'}`}>
          {statusLabels[statusKey] || statusKey}
        </span>
        <svg className="w-4 h-4 text-gray-400 group-hover:text-[#1cbe93] transition-colors hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

function SkeletonLoader() {
  return (
    <div className="mt-6 px-4 sm:px-6 lg:px-9">
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-4 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="h-6 w-48 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-4 w-24 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 sm:h-24 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DepositAccountStats() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/user/deposit-accounts-stats`,
        { headers: userAuthHeader() }
      );
      if (response.data && response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching deposit account stats:', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (isError) {
    return (
      <div className="mt-6 px-4 sm:px-6 lg:px-9">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-gray-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm">Les données Dépôt de Comptes sont temporairement indisponibles</span>
            </div>
            <button
              onClick={fetchStats}
              className="px-3 py-1.5 text-xs font-medium text-[#1cbe93] border border-[#1cbe93] rounded-lg hover:bg-[#1cbe93]/5 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const { statusBreakdown, totalFilings, totalDrafts, totalAmountPaid, recentFilings } = stats;
  const totalAll = totalFilings + totalDrafts;

  return (
    <div className="mt-6 px-4 sm:px-6 lg:px-9">
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Section Header */}
        <div className="p-4 sm:p-5 lg:p-6 pb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-[#1cbe93] to-[#17a37e] flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">
                  Dépôt de Comptes
                </h4>
                <p className="text-[11px] sm:text-xs text-gray-500">
                  Suivez vos dépôts de comptes annuels
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Summary pill */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200">
                <span className="text-[11px] sm:text-xs text-gray-500">Total</span>
                <span className="text-sm sm:text-base font-bold text-gray-800">{totalAll}</span>
              </div>
              {totalAmountPaid > 0 && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
                  <span className="text-[11px] sm:text-xs text-emerald-600">Payé</span>
                  <span className="text-sm font-bold text-emerald-700">{totalAmountPaid.toFixed(2)} €</span>
                </div>
              )}
              <Link
                to="/prouser/depot-comptes"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#1cbe93] hover:text-[#17a37e] border border-[#1cbe93]/30 hover:border-[#1cbe93] rounded-lg transition-all hover:bg-[#1cbe93]/5"
              >
                Voir tout
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="px-4 sm:px-5 lg:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <StatCard
              label={STATUS_CONFIG.draft.label}
              count={statusBreakdown.draft + totalDrafts}
              {...STATUS_CONFIG.draft}
            />
            <StatCard
              label={STATUS_CONFIG.signed.label}
              count={statusBreakdown.signed}
              {...STATUS_CONFIG.signed}
            />
            <StatCard
              label={STATUS_CONFIG.submitted.label}
              count={statusBreakdown.submitted}
              {...STATUS_CONFIG.submitted}
            />
            <StatCard
              label={STATUS_CONFIG.validated.label}
              count={statusBreakdown.validated}
              {...STATUS_CONFIG.validated}
            />
            <StatCard
              label={STATUS_CONFIG.rejected.label}
              count={statusBreakdown.rejected}
              {...STATUS_CONFIG.rejected}
            />
          </div>

          {/* Amount paid on mobile */}
          {totalAmountPaid > 0 && (
            <div className="sm:hidden mt-3 flex items-center justify-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-emerald-600">Montant total payé:</span>
              <span className="text-sm font-bold text-emerald-700">{totalAmountPaid.toFixed(2)} €</span>
            </div>
          )}
        </div>

        {/* Recent Filings */}
        {recentFilings && recentFilings.length > 0 && (
          <div className="mt-4 sm:mt-5 px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6">
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-xs sm:text-sm font-semibold text-gray-600">Dépôts récents</h5>
                <Link
                  to="/prouser/depot-comptes"
                  className="text-[11px] sm:text-xs text-[#1cbe93] hover:text-[#17a37e] font-medium transition-colors"
                >
                  Tout voir →
                </Link>
              </div>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 overflow-hidden bg-white">
                {recentFilings.map((filing) => (
                  <RecentFilingRow key={filing.id} filing={filing} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {(!recentFilings || recentFilings.length === 0) && totalAll === 0 && (
          <div className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6">
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                <svg className="w-10 h-10 mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <p className="text-sm font-medium text-gray-500 mb-1">Aucun dépôt de comptes</p>
                <p className="text-xs text-gray-400 mb-3">Commencez votre premier dépôt de comptes annuels</p>
                <Link
                  to="/prouser/depot-comptes"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-[#1cbe93] to-[#17a37e] rounded-lg hover:shadow-md transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Commencer un dépôt
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
